import { useEffect, useState } from "react";

import { useAppStore } from "../../../../../context/store";
import { Dialog, Portal, CloseButton, Button, Field, Input, Textarea, Spinner, Select, createListCollection, HStack, Box } from "@chakra-ui/react";

import { serviceCreateOrEditProjects } from "../../../../../api/services/project";
import Type from "../../../../../utilies/Types";
import { buildCode, floatingStyles, getCombinedBBox, removeEpochSuffix, removeFileExtension, showToast } from "../../../../../utilies/Utility";
import { LuCopy, LuFolders, LuPlus, LuTowerControl, LuUsers } from "react-icons/lu";
import { getEditedGeoJSON } from "../../../../../utilies/geojson/GetEditedGeojson";
import { getProjects } from "../../../../../utilies/services/project/GetProject";
import { useMapObjects } from "../../../../../context/MapObjectsContext";
import { saveLayerToServerUnified } from "../../../../../utilies/loader/layer/saveLayerToServer";

const roleOptions = createListCollection({
  items: [
    { label: "Annotator", value: "annotator" },
    { label: "Reviewer", value: "reviewer" },
    { label: "Viewer", value: "viewer" },
    { label: "Digitizer", value: "digitizer" },
    { label: "Super Admin", value: "superadmin" },
  ],
});

const CreateProjectDialog = () => {
  const createProjectDialog = useAppStore((s) => s.createProjectDialog);
  const isShowDuplicateProjectDialog = useAppStore((s) => s.isShowDuplicateProjectDialog);
  const setIsShowDuplicateProjectDialog = useAppStore((s) => s.setIsShowDuplicateProjectDialog);
  const city = useAppStore((s) => s.city);
  const userData = useAppStore((s) => s.userData);
  const users = useAppStore((s) => s.users);
  const { mapObjects } = useMapObjects();
  const pointCloudStyle = useAppStore((s) => s.pointCloudStyle);
  const setCreateProjectDialog = useAppStore.getState().setCreateProjectDialog;
  const setProjects = useAppStore.getState().setProjects;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [contributors, setContributors] = useState([]);
  const [contributorRoles, setContributorRoles] = useState({});
  const [selectedCity, setSelectedCity] = useState("");
  const [code, setCode] = useState("");

  const [isProgress, setIsProgress] = useState(false);

  const [contributorsOptions, setContributorsOptions] = useState(createListCollection({ items: [] }));

  useEffect(() => {
    if (users) {
      const newContributors = createListCollection({
        items: Object.values(users)
          .sort((a, b) => a.username.localeCompare(b.username))
          .map((user) => ({
            label: (`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username) + " (" + user.username + ")",
            value: user.username,
          })),
      });

      setContributorsOptions(newContributors);
    }
  }, [users]);

  const [cityOptions, setCityOptions] = useState(
    createListCollection({
      items: [],
    }),
  );

  useEffect(() => {
    if (city) {
      let temp = [];
      for (const key in city) {
        temp.push({ label: city[key].name, value: city[key].name.toLowerCase() });
      }
      setCityOptions(
        createListCollection({
          items: temp,
          itemToString: (item) => item.label,
          itemToValue: (item) => item.value,
        }),
      );
    }
  }, [city]);

  const createProject = () => {
    if (name.trim() === "") {
      showToast("Project name is required", Type.NOTIFICATION.WARNING);
      return;
    }
    if (description.trim() === "") {
      showToast("Project description is required", Type.NOTIFICATION.WARNING);
      return;
    }
    if (contributors.length === 0) {
      showToast("Project contributors is required", Type.NOTIFICATION.WARNING);
      return;
    }

    setIsProgress(true);

    const listOfLayersId = [];
    let listOfGeojsons = [];
    for (const key in mapObjects) {
      if (mapObjects[key].layer_id && !mapObjects[key].project_id) {
        listOfLayersId.push(mapObjects[key].layer_id);
        let g = getEditedGeoJSON(mapObjects[key]);
        listOfGeojsons.push(g);
      }
    }

    const contributorsPayload = contributors.map((username) => ({
      username,
      role: contributorRoles[username] || "viewer",
    }));

    const formData = new FormData();
    formData.append(
      "data",
      JSON.stringify({
        project_name: name,
        project_code: code,
        description: description,
        username: userData?.user?.username,
        contributors: contributorsPayload,
        layers: listOfLayersId,
        project_city: selectedCity,
        bbox: getCombinedBBox(listOfGeojsons),
      }),
    );

    serviceCreateOrEditProjects(formData, userData.token)
      .then((res) => {
        setIsProgress(false);

        if (res.data.status == "success") {
          setCreateProjectDialog(false);
          showToast("The new project has been created", Type.NOTIFICATION.SUCCESS);

          getProjects({
            userData,
            setProjects,
          });
        } else {
          showToast(res.data.message, Type.NOTIFICATION.WARNING);
        }
      })
      .catch((err) => {
        setIsProgress(false);
        console.error("Error", err);
      });
  };

  const duplicateProject = async () => {
    let item = isShowDuplicateProjectDialog.project;
    const projectId = item.id;

    let listOfLayersId = [];
    for (const key in mapObjects) {
      if (mapObjects[key].project_id != projectId) continue;
      await saveLayerToServerUnified({
        layer: mapObjects[key],
        userData,
        pointCloudStyle,
        description: "",
        createFileBaseName: removeEpochSuffix(removeFileExtension(mapObjects[key].file_name)),
        sendLayerId: false,
        attachFile: "always",
      })
        .then((res) => {
          if (res.data.status == "failed") {
            showToast(res.data.message, Type.NOTIFICATION.WARNING);
          } else {
            showToast("Stored " + removeEpochSuffix(removeFileExtension(res.data.layer_name)), Type.NOTIFICATION.SUCCESS);
            listOfLayersId.push(res.data.layer_id);
          }
        })
        .catch((err) => {
          console.error("Error", err);
          setIsProgress(false);
        });
    }

    const contributorsPayload = contributors.map((username) => ({
      username,
      role: contributorRoles[username] || "viewer",
    }));

    const formData = new FormData();

    formData.append(
      "data",
      JSON.stringify({
        project_name: name,
        project_code: code,
        description: description,
        username: userData?.user?.username,
        contributors: contributorsPayload,
        layers: [...new Set(listOfLayersId)],
        project_city: selectedCity,
        bbox: [],
      }),
    );

    serviceCreateOrEditProjects(formData, userData.token)
      .then((res) => {
        if (res.data.status == "success") {
          showToast(res.data.message, Type.NOTIFICATION.SUCCESS);
          getProjects({
            userData,
            setProjects,
            onSuccess: () => {
              setIsProgress(false);
              setCreateProjectDialog(false);
            },
            onError: () => {
              setIsProgress(false);
            },
          });
        }
      })
      .catch((err) => {
        showToast(err.message, Type.NOTIFICATION.WARNING);
        console.error("Error", err);
      });
  };

  return (
    <Dialog.Root
      lazyMount
      closeOnInteractOutside={false}
      open={createProjectDialog}
      onOpenChange={(e) => {
        setCreateProjectDialog(e.open);
        if (!e.open) {
          setIsShowDuplicateProjectDialog({
            open: false,
            project: null,
          });
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title> {isShowDuplicateProjectDialog?.open ? "Duplicate Project" : "Create New Project"} </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Field.Root>
                <Field.Label>Project Name</Field.Label>
                <Input
                  placeholder="Enter project name"
                  value={name}
                  onChange={(v) => {
                    setName(v.target.value);
                    setCode(buildCode(v.target.value));
                  }}
                />
              </Field.Root>
              <Field.Root mt={4}>
                <Field.Label>Project Code</Field.Label>
                <Input
                  placeholder="Enter project code"
                  value={code}
                  onChange={(v) => {
                    setCode(v.target.value);
                  }}
                />
              </Field.Root>
              <Field.Root mt={4}>
                <Field.Label>Description</Field.Label>
                <Textarea
                  placeholder=" ... "
                  variant="outline"
                  rows={3}
                  value={description}
                  onChange={(v) => {
                    setDescription(v.target.value);
                  }}
                />
              </Field.Root>

              <Field.Root mt={5}>
                <Box pos="relative" w="full">
                  <Select.Root
                    collection={cityOptions}
                    size="sm"
                    width="full"
                    onValueChange={(v) => {
                      setSelectedCity(v.value[0]);
                    }}
                  >
                    <Select.HiddenSelect />

                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder={"City"} />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.ClearTrigger />
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>

                    <Select.Positioner>
                      <Select.Content>
                        {cityOptions.items.map((item) => (
                          <Select.Item item={item} key={item.value}>
                            {item.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                  <Field.Label css={floatingStyles}>
                    <LuTowerControl /> City
                  </Field.Label>
                </Box>
              </Field.Root>

              <Select.Root
                multiple
                collection={contributorsOptions}
                size="sm"
                width="full"
                mt={3}
                mb={3}
                onValueChange={(val) => {
                  const selectedUsernames = val.value;

                  setContributors(selectedUsernames);

                  setContributorRoles((prev) => {
                    const next = { ...prev };

                    Object.keys(next).forEach((username) => {
                      if (!selectedUsernames.includes(username)) {
                        delete next[username];
                      }
                    });

                    selectedUsernames.forEach((username) => {
                      if (!next[username]) {
                        next[username] = "viewer";
                      }
                    });

                    return next;
                  });
                }}
              >
                <Select.HiddenSelect />
                <Select.Label>
                  <HStack>
                    <LuUsers /> Contributors
                  </HStack>
                </Select.Label>
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder="Select Contributors" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.ClearTrigger />
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>

                <Select.Positioner>
                  <Select.Content>
                    {contributorsOptions.items.map((framework) => (
                      <Select.Item item={framework} key={framework.value}>
                        {framework.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>

              {contributors.length > 0 && (
                <Box mt={5}>
                  {contributors.map((username) => (
                    <HStack key={username} justify="space-between" mt={2}>
                      <Box fontSize="sm">{username}</Box>
                      <Select.Root
                        width={"50%"}
                        size="xs"
                        collection={roleOptions}
                        value={[contributorRoles[username] || "viewer"]}
                        onValueChange={(v) => {
                          const role = v.value[0];
                          setContributorRoles((prev) => ({
                            ...prev,
                            [username]: role,
                          }));
                        }}
                      >
                        <Select.HiddenSelect />
                        <Select.Control>
                          <Select.Trigger>
                            <Select.ValueText placeholder="Role" />
                          </Select.Trigger>
                          <Select.IndicatorGroup>
                            <Select.Indicator />
                          </Select.IndicatorGroup>
                        </Select.Control>
                        <Select.Positioner>
                          <Select.Content>
                            {roleOptions.items.map((item) => (
                              <Select.Item key={item.value} item={item}>
                                {item.label}
                                <Select.ItemIndicator />
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Positioner>
                      </Select.Root>
                    </HStack>
                  ))}
                </Box>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline">Cancel</Button>
              </Dialog.ActionTrigger>
              <Button disabled={isProgress} onClick={isShowDuplicateProjectDialog?.open ? duplicateProject : createProject}>
                {isProgress ? (
                  <Spinner size={"sm"} />
                ) : isShowDuplicateProjectDialog?.open ? (
                  <>
                    <LuFolders />
                    Duplicate Project
                  </>
                ) : (
                  <>
                    <LuPlus />
                    Create Project
                  </>
                )}
              </Button>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default CreateProjectDialog;
